import ReactGA from 'react-ga4';

export interface ClickContext {
  page_pattern: string;
  userid?: string | number;
  vm_visitor_id?: string | number;
  vm_visitor_email?: string;
  vm_account_dealerid?: string | number;
  vm_account_name?: string;
}

export type ClickContextGetter = () => ClickContext;

interface ClickPayload {
  track_id: string;
  track_section?: string;
  track_label?: string;
  track_target?: string;
  track_value?: string;
  element_tag?: string;
  element_type?: string;
}

// Storage can be unavailable (blocked site data, some test runtimes) — a debug
// toggle must never throw inside the global click listener and kill tracking.
function isDebugTrackingEnabled(): boolean {
  if (!import.meta.env.DEV) return false;
  try {
    return globalThis.localStorage?.getItem('debug_tracking') === 'true';
  } catch {
    return false;
  }
}

const DATA_TRACK_ATTR = 'data-track';
const DATA_TRACK_PREFIX = 'data-track-';

const INTERACTIVE_SELECTORS = [
  'button',
  'a',
  'input[type="button"]',
  'input[type="submit"]',
  'input[type="checkbox"]',
  'input[type="radio"]',
  'select',
  '[role="button"]',
  '[role="link"]',
  '[role="tab"]',
  '[role="menuitem"]',
  '[role="checkbox"]',
  '[role="switch"]',
  '[role="option"]',
].join(',');

let activeListener: ((e: MouseEvent) => void) | null = null;
let contextGetter: ClickContextGetter | null = null;

function getTextContent(el: HTMLElement): string {
  const text = el.textContent?.trim() ?? '';
  return text.length > 100 ? text.slice(0, 100) : text;
}

function findTrackableAncestor(target: EventTarget | null): HTMLElement | null {
  let el = target as HTMLElement | null;
  while (el && el !== document.documentElement) {
    if (el.hasAttribute?.(DATA_TRACK_ATTR)) {
      return el;
    }
    el = el.parentElement;
  }
  return null;
}

function findInteractiveAncestor(target: EventTarget | null): HTMLElement | null {
  let el = target as HTMLElement | null;
  while (el && el !== document.documentElement) {
    if (el.matches?.(INTERACTIVE_SELECTORS)) {
      return el;
    }
    el = el.parentElement;
  }
  return null;
}

function deriveTrackId(el: HTMLElement): string {
  const ariaLabel = el.getAttribute('aria-label');
  if (ariaLabel) {
    return ariaLabel
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_|_$/g, '');
  }

  const text = getTextContent(el);
  if (text) {
    return text
      .slice(0, 50)
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_|_$/g, '');
  }

  const tag = el.tagName.toLowerCase();
  const role = el.getAttribute('role');
  const type = (el as HTMLInputElement).type;
  return [tag, role, type].filter(Boolean).join('_');
}

function deriveElementTarget(el: HTMLElement): string | undefined {
  const tag = el.tagName.toLowerCase();
  if (tag === 'a') return 'navigate';
  if (tag === 'select') return 'select';
  if (tag === 'input') {
    const type = (el as HTMLInputElement).type;
    if (type === 'checkbox' || type === 'radio') return 'toggle';
    if (type === 'submit') return 'submit';
  }
  const role = el.getAttribute('role');
  if (role === 'tab') return 'tab_switch';
  if (role === 'checkbox' || role === 'switch') return 'toggle';
  if (role === 'menuitem' || role === 'option') return 'select';
  return undefined;
}

function extractPayload(el: HTMLElement, hasExplicitTrack: boolean): ClickPayload {
  if (hasExplicitTrack) {
    const trackId = el.getAttribute(DATA_TRACK_ATTR) ?? '';
    const section = el.getAttribute(`${DATA_TRACK_PREFIX}section`) ?? undefined;
    const label = el.getAttribute(`${DATA_TRACK_PREFIX}label`) || getTextContent(el) || undefined;
    const target = el.getAttribute(`${DATA_TRACK_PREFIX}target`) ?? undefined;
    const value = el.getAttribute(`${DATA_TRACK_PREFIX}value`) ?? undefined;

    return {
      track_id: trackId,
      track_section: section,
      track_label: label,
      track_target: target,
      track_value: value,
      element_tag: el.tagName.toLowerCase(),
    };
  }

  return {
    track_id: deriveTrackId(el),
    track_label: el.getAttribute('aria-label') || getTextContent(el) || undefined,
    track_target: deriveElementTarget(el),
    element_tag: el.tagName.toLowerCase(),
    element_type: (el as HTMLInputElement).type || el.getAttribute('role') || undefined,
  };
}

function handleClick(e: MouseEvent) {
  const explicitEl = findTrackableAncestor(e.target);

  if (explicitEl) {
    const payload = extractPayload(explicitEl, true);
    const context = contextGetter?.() ?? { page_pattern: '' };
    const eventParams = { ...payload, ...context };

    if (isDebugTrackingEnabled()) {
      console.debug('[ClickTracker:explicit]', payload.track_id, eventParams);
    }

    ReactGA.gtag('event', 'element_click', eventParams);
    return;
  }

  const interactiveEl = findInteractiveAncestor(e.target);
  if (!interactiveEl) return;

  const payload = extractPayload(interactiveEl, false);
  const context = contextGetter?.() ?? { page_pattern: '' };
  const eventParams = { ...payload, ...context };

  if (isDebugTrackingEnabled()) {
    console.debug('[ClickTracker:auto]', payload.track_id, eventParams);
  }

  ReactGA.gtag('event', 'element_click', eventParams);
}

export function initClickTracker(getContext: ClickContextGetter): void {
  teardownClickTracker();
  contextGetter = getContext;
  activeListener = handleClick;
  document.addEventListener('click', activeListener, true);
}

export function teardownClickTracker(): void {
  if (activeListener) {
    document.removeEventListener('click', activeListener, true);
    activeListener = null;
  }
  contextGetter = null;
}
